/**
 * English number words to a value, one well-formed number at a time.
 *
 * A run of number words is cut wherever the grammar stops allowing a continuation:
 * "twenty thirty-five" is two numbers, "three four five" is three, and "twenty and
 * thirty-five" is two numbers with an ordinary "and" between them. "And" belongs to a
 * number only right after hundred or a scale word ("one hundred and five"). Two numbers
 * that together read as a year ("nineteen ninety-nine", "twenty twenty-six") are joined
 * into one year.
 */

const UNIT_WORDS: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
};

const TENS_WORDS: Record<string, number> = {
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};

const SCALE_WORDS: Record<string, number> = {
  thousand: 1000,
  million: 1_000_000,
  billion: 1_000_000_000,
  trillion: 1_000_000_000_000,
};

const HUNDRED = "hundred";

/** Years are first spoken as a pair: "eleven" … "twenty", then "ten" … "ninety-nine". */
const YEAR_FIRST_MIN = 11;
const YEAR_FIRST_MAX = 20;
const YEAR_SECOND_MIN = 10;
const YEAR_SECOND_MAX = 99;

export function isNumberWord(word: string): boolean {
  return (
    UNIT_WORDS[word] !== undefined ||
    TENS_WORDS[word] !== undefined ||
    SCALE_WORDS[word] !== undefined ||
    word === HUNDRED
  );
}

type Kind =
  | "start"
  | "zero"
  | "unit"
  | "teen"
  | "tens"
  | "hundred"
  | "scale"
  | "and";

interface NumState {
  chunk: number;
  hasHundred: boolean;
  lastScale: number;
  prev: Kind;
  total: number;
}

const START: NumState = {
  total: 0,
  chunk: 0,
  prev: "start",
  hasHundred: false,
  lastScale: Number.POSITIVE_INFINITY,
};

function addUnit(state: NumState, token: string): NumState | null {
  const value = UNIT_WORDS[token];
  if (value === 0) {
    return state.prev === "start" ? { ...state, prev: "zero" } : null;
  }
  if (value >= 10) {
    const allowed: Kind[] = ["start", "hundred", "scale", "and"];
    return allowed.includes(state.prev)
      ? { ...state, chunk: state.chunk + value, prev: "teen" }
      : null;
  }
  const allowed: Kind[] = ["start", "tens", "hundred", "scale", "and"];
  return allowed.includes(state.prev)
    ? { ...state, chunk: state.chunk + value, prev: "unit" }
    : null;
}

function addHundred(state: NumState): NumState | null {
  if (state.prev === "start") {
    return { ...state, chunk: 100, hasHundred: true, prev: "hundred" };
  }
  const allowed: Kind[] = ["unit", "teen", "tens"];
  if (!allowed.includes(state.prev) || state.hasHundred) {
    return null;
  }
  return {
    ...state,
    chunk: state.chunk * 100,
    hasHundred: true,
    prev: "hundred",
  };
}

function addScale(state: NumState, scale: number): NumState | null {
  const allowed: Kind[] = ["start", "unit", "teen", "tens", "hundred"];
  if (!allowed.includes(state.prev) || scale >= state.lastScale) {
    return null;
  }
  return {
    total: state.total + (state.chunk === 0 ? 1 : state.chunk) * scale,
    chunk: 0,
    hasHundred: false,
    lastScale: scale,
    prev: "scale",
  };
}

function addToken(state: NumState, token: string): NumState | null {
  if (token === "and") {
    return state.prev === "hundred" || state.prev === "scale"
      ? { ...state, prev: "and" }
      : null;
  }
  if (UNIT_WORDS[token] !== undefined) {
    return addUnit(state, token);
  }
  const tens = TENS_WORDS[token];
  if (tens !== undefined) {
    const allowed: Kind[] = ["start", "hundred", "scale", "and"];
    return allowed.includes(state.prev)
      ? { ...state, chunk: state.chunk + tens, prev: "tens" }
      : null;
  }
  if (token === HUNDRED) {
    return addHundred(state);
  }
  const scale = SCALE_WORDS[token];
  return scale === undefined ? null : addScale(state, scale);
}

function stateValue(state: NumState): number {
  return state.total + state.chunk;
}

export interface NumberSpan {
  /** Index of the first word of the number. */
  from: number;
  /** Index of the last word of the number. */
  to: number;
  /** The digits, ready to print. */
  value: string;
}

interface SpanBuilder {
  from: number;
  lastNumberWord: number;
  state: NumState;
}

function isLowOrder(word: string[]): boolean {
  const first = word[0];
  return UNIT_WORDS[first] !== undefined || TENS_WORDS[first] !== undefined;
}

function addWord(state: NumState, parts: string[]): NumState | null {
  let next: NumState | null = state;
  for (const part of parts) {
    if (next === null) {
      return null;
    }
    next = addToken(next, part);
  }
  return next;
}

function isYearPair(first: NumberSpan, second: NumberSpan): boolean {
  if (second.from !== first.to + 1) {
    return false;
  }
  const a = Number(first.value);
  const b = Number(second.value);
  return (
    a >= YEAR_FIRST_MIN &&
    a <= YEAR_FIRST_MAX &&
    b >= YEAR_SECOND_MIN &&
    b <= YEAR_SECOND_MAX
  );
}

function joinYearPairs(spans: NumberSpan[]): NumberSpan[] {
  const joined: NumberSpan[] = [];
  for (const span of spans) {
    const last = joined.at(-1);
    if (last !== undefined && isYearPair(last, span)) {
      joined[joined.length - 1] = {
        from: last.from,
        to: span.to,
        value: `${last.value}${span.value}`,
      };
    } else {
      joined.push(span);
    }
  }
  return joined;
}

function flush(open: SpanBuilder | null, spans: NumberSpan[]): null {
  if (open !== null) {
    spans.push({
      from: open.from,
      to: open.lastNumberWord,
      value: String(stateValue(open.state)),
    });
  }
  return null;
}

/** Applies one word to the open number, or starts a new one when it cannot continue. */
function stepWord(
  open: SpanBuilder | null,
  index: number,
  parts: string[],
  spans: NumberSpan[]
): SpanBuilder | null {
  const continued = open === null ? null : addWord(open.state, parts);
  if (open !== null && continued !== null) {
    return { ...open, state: continued, lastNumberWord: index };
  }
  flush(open, spans);
  const fresh = addWord(START, parts);
  return fresh === null
    ? null
    : { from: index, lastNumberWord: index, state: fresh };
}

/** An "and" stays inside a number only between hundred or a scale and a small number. */
function stepAnd(
  open: SpanBuilder | null,
  next: string[] | undefined,
  nextAdjacent: boolean,
  spans: NumberSpan[]
): SpanBuilder | null {
  if (open !== null && nextAdjacent && next !== undefined && isLowOrder(next)) {
    const state = addToken(open.state, "and");
    if (state !== null) {
      return { ...open, state };
    }
  }
  return flush(open, spans);
}

/**
 * Finds the numbers in a list of words. Each entry is one source word split at its
 * hyphens ("forty-two" is ["forty", "two"]), lower-cased. `adjacent[i]` says word i
 * follows word i-1 with only white space between (default: it does), so punctuation
 * or another word between two number words keeps them apart.
 */
export function findNumberSpans(
  words: string[][],
  adjacent: boolean[] = []
): NumberSpan[] {
  const spans: NumberSpan[] = [];
  let open: SpanBuilder | null = null;
  for (let i = 0; i < words.length; i += 1) {
    const parts = words[i];
    if (adjacent[i] === false) {
      open = flush(open, spans);
    }
    if (parts.length === 1 && parts[0] === "and") {
      open = stepAnd(open, words[i + 1], adjacent[i + 1] !== false, spans);
    } else if (parts.every(isNumberWord)) {
      open = stepWord(open, i, parts, spans);
    } else {
      open = flush(open, spans);
    }
  }
  flush(open, spans);
  return joinYearPairs(spans);
}

/** The digits when all of `tokens` is one number (or one year pair), else null. */
export function parseWholeNumberWords(tokens: string[]): string | null {
  const spans = findNumberSpans(tokens.map((token) => [token]));
  if (
    spans.length !== 1 ||
    spans[0].from !== 0 ||
    spans[0].to !== tokens.length - 1
  ) {
    return null;
  }
  return spans[0].value;
}
